/**
 * 路由 path → 埋点事件信息 映射工具
 *
 * 路由 name 按父子层级拼接为 i18n key，例如：
 *   /dashboard/workplace             → { key: 'menu.home.workplace',              type: 'menu'   }
 *   /system/permission/role/add      → { key: 'menu.system.permission.role.add',  type: 'button' }
 *
 * 操作型路由（name 命中下列关键词）视为 button，其余视为 menu
 */
import type { ClickEventType } from '@ai-saas/tracker';

const BUTTON_KEYWORDS = ['add', 'edit', 'create', 'detail', 'view', 'copy'];
const EXCLUDED_KEYS = [
  'menu.app.appName',
  'menu.app.mgmt.appName',
  'menu.data.dataset.detail',
  'menu.data.dataset.detail.file',
];
const isButtonRoute = (name: string) => {
  const segments = name.split('.');
  return segments.some((seg) => BUTTON_KEYWORDS.includes(seg));
};

interface RouteClickEvent {
  clickEventKey: string;
  clickEventType: ClickEventType;
}

/** path → 点击埋点信息 */
const pathClickEventMap = new Map<string, RouteClickEvent>();
/** 隐藏路由绝对路径 → 应高亮的父菜单路径（来自路由 parentKeys[0]） */
const parentKeyMenuMap = new Map<string, string>();

/** 单次遍历路由树，同时建立两张映射表 */
function buildMaps(routes: any[], parentPath = '', parentKey = 'menu') {
  for (const route of routes) {
    const routePath: string = route.path ?? '';
    const fullPath = routePath.startsWith('/')
      ? routePath
      : `${parentPath}/${routePath}`.replace(/\/+/g, '/');

    // parentKeys 映射（hideInMenu 路由 → 父菜单路径）
    if (route.parentKeys?.length) {
      parentKeyMenuMap.set(fullPath, route.parentKeys[0]);
    }

    // 点击埋点映射（有 name 的路由）
    if (route.name) {
      const menuKey = `${parentKey}.${route.name}`;
      const clickEventType: ClickEventType = isButtonRoute(route.name) ? 2 : 1;
      pathClickEventMap.set(fullPath, { clickEventKey: menuKey, clickEventType });
      if (route.routes) buildMaps(route.routes, fullPath, menuKey);
    } else {
      if (route.routes) buildMaps(route.routes, fullPath, parentKey);
    }
  }
}

/** 在 patchClientRoutes 中调用一次，初始化所有映射表 */
export function initRouteMaps(routes: any[]) {
  pathClickEventMap.clear();
  parentKeyMenuMap.clear();
  buildMaps(routes);
}

/** 根据当前路径获取应高亮的父菜单路径，找不到则返回 undefined */
export function getMenuPathByRoute(path: string): string | undefined {
  return parentKeyMenuMap.get(path);
}

/**
 * 判断实际路径是否匹配路由模式（支持 :param 动态段和 * 通配符）
 * 例如：/system/org/company/edit/22 匹配 /system/org/company/edit/:id
 */
function matchPattern(pattern: string, path: string): boolean {
  const patternSegs = pattern.split('/');
  const pathSegs = path.split('/');
  if (patternSegs.length !== pathSegs.length) {
    const lastSeg = patternSegs[patternSegs.length - 1];
    if (lastSeg !== '*' && !lastSeg?.startsWith(':')) return false;
  }
  for (let i = 0; i < patternSegs.length; i++) {
    const ps = patternSegs[i];
    if (ps === '*') return true;
    if (ps?.startsWith(':')) continue;
    if (ps !== pathSegs[i]) return false;
  }
  return patternSegs.length === pathSegs.length;
}

/** 根据当前 pathname 获取点击事件信息，支持动态路由参数，找不到返回 undefined */
export function getClickEventByPath(path: string): RouteClickEvent | undefined {
  const exact = pathClickEventMap.get(path);
  if (exact) return EXCLUDED_KEYS.includes(exact.clickEventKey) ? undefined : exact;
  for (const [pattern, event] of pathClickEventMap) {
    if (pattern.includes(':') || pattern.includes('*')) {
      if (matchPattern(pattern, path)) return EXCLUDED_KEYS.includes(event.clickEventKey) ? undefined : event;
    }
  }
  return undefined;
}
